import { useTranslations } from "next-intl";
import { type Resolver, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import type { CountryCode } from "@/config/country-config";
import { useCountry } from "@/config/country-context";
import { getCountryRules } from "@/config/country-rules";
import { useRefinanceScenario } from "@/features/refinance/hooks/use-refinance-scenario";
import { RefinanceContext, RefinanceDebt } from "@/features/refinance/types";
import {
	findReportedAmountIssue,
	isCommittedDebt,
} from "@/features/refinance/utils/debt-status";
import {
	isValidOcr,
	validateBankgiro,
	validatePlusgiro,
} from "@/features/refinance/utils/giro";
import {
	type RefinanceOfferParts,
	findRefinanceDebtIssues,
} from "@/features/refinance/utils/refinance-allocation";
// Not the `@/hooks` barrel: it re-exports `useSubmitTask`, whose server action
// chain cannot be imported by the unit tests that cover this schema.
import { useCurrency } from "@/hooks/use-currency";
import { hasFinnishIbanFormat, isSameIban } from "@/shared/finnish-iban";
import { isValidFinnishReference } from "@/shared/finnish-reference";

export interface RefinanceForm {
	appliedAmount?: number;
	debts: RefinanceDebt[];
}

interface UseRefinanceFormArgs {
	context: RefinanceContext;
}

export interface DebtValidationMessages {
	required: string;
	amountAboveZero: string;
	amountAboveOriginal: (originalAmount: number) => string;
	invalidPlusgiro: string;
	invalidBankgiro: string;
	invalidGiroCheckDigit: string;
	invalidIban: string;
	ibanIsOwnAccount: string;
	invalidOcr: string;
}

/** The customer's own payout account, as the refinance task reports it. */
type CustomerAccountNumber = string | null | undefined;

type RedemptionIssue = { field: "iban" | "loanNumber"; message: string };

/**
 * The giro and OCR rules, which only apply to a debt Lea Bank has to pay off
 * externally. Kept out of the schema's `superRefine` so the debt card can ask
 * for them on a debt whose other fields are still empty - Zod skips a
 * refinement once the object shape itself has failed.
 */
const findRedemptionIssues = (
	debt: Pick<
		RefinanceDebt,
		"internalRedemption" | "giroType" | "iban" | "loanNumber"
	>,
	messages: DebtValidationMessages,
	country: CountryCode,
	customerAccountNumber?: CustomerAccountNumber
): RedemptionIssue[] => {
	if (debt.internalRedemption) return [];

	const issues: RedemptionIssue[] = [];
	const isGiroOcr = getCountryRules(country).redemptionPayment === "giro-ocr";

	if (isGiroOcr) {
		const isPlusgiro = debt.giroType === "plusgiro";

		if (!debt.iban) {
			issues.push({ field: "iban", message: messages.required });
		} else {
			const validity = isPlusgiro
				? validatePlusgiro(debt.iban)
				: validateBankgiro(debt.iban);

			if (validity !== "valid") {
				issues.push({
					field: "iban",
					message:
						validity === "checkDigit"
							? messages.invalidGiroCheckDigit
							: isPlusgiro
								? messages.invalidPlusgiro
								: messages.invalidBankgiro,
				});
			}
		}
	} else if (!debt.iban) {
		issues.push({ field: "iban", message: messages.required });
	} else if (!hasFinnishIbanFormat(debt.iban)) {
		issues.push({ field: "iban", message: messages.invalidIban });
	} else if (isSameIban(debt.iban, customerAccountNumber ?? "")) {
		issues.push({ field: "iban", message: messages.ibanIsOwnAccount });
	}

	if (!debt.loanNumber) {
		issues.push({ field: "loanNumber", message: messages.required });
	} else if (
		isGiroOcr
			? !isValidOcr(debt.loanNumber)
			: !isValidFinnishReference(debt.loanNumber)
	) {
		issues.push({ field: "loanNumber", message: messages.invalidOcr });
	}

	return issues;
};

export const buildDebtItemSchema = (
	messages: DebtValidationMessages,
	country: CountryCode,
	customerAccountNumber?: CustomerAccountNumber
) => {
	const { required } = messages;

	return z
		.object({
			description: z.string({ error: required }).min(1, required),
			bank: z.string({ error: required }).min(1, required),
			// The number field reports an emptied input as `null`, which coerces to 0.
			amount: z.preprocess(
				(value) => (value === null || value === "" ? undefined : value),
				z.coerce.number({ error: required }).positive(messages.amountAboveZero)
			),
			originalAmount: z.number().optional(),
			iban: z.string().optional(),
			loanNumber: z.string().optional(),
			internalRedemption: z.boolean().optional(),
			isReported: z.boolean().optional(),
			fromRegister: z.boolean().optional(),
			monthlyAmount: z.number().optional(),
			giroType: z.enum(["bankgiro", "plusgiro"]).optional(),
			saved: z.boolean().optional(),
		})
		.superRefine((debt, ctx) => {
			for (const issue of findRedemptionIssues(
				debt,
				messages,
				country,
				customerAccountNumber
			)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: issue.message,
					path: [issue.field],
				});
			}
		});
};

// A debt either parses or it does not, whatever the messages say, so the debt
// card can gate its own Save button on the submit-time rules without asking for
// translations.
const unusedMessages: DebtValidationMessages = {
	required: "",
	amountAboveZero: "",
	amountAboveOriginal: () => "",
	invalidPlusgiro: "",
	invalidBankgiro: "",
	invalidGiroCheckDigit: "",
	invalidIban: "",
	ibanIsOwnAccount: "",
	invalidOcr: "",
};

export interface DebtIssue {
	field: keyof RefinanceDebt;
	message: string;
}

const findAmountIssue = (
	debt: RefinanceDebt,
	messages: DebtValidationMessages
): DebtIssue | undefined => {
	const issue = findReportedAmountIssue(debt);
	if (!issue) return undefined;

	return {
		field: "amount",
		message:
			issue.kind === "notPositive"
				? messages.amountAboveZero
				: messages.amountAboveOriginal(issue.originalAmount),
	};
};

/**
 * Every field a debt fails as if it were committed, with the first message for
 * each. The redemption details are checked even while the rest of the debt is
 * empty, which the schema's own refinement would skip.
 */
export const findDebtIssues = (
	debt: RefinanceDebt,
	messages: DebtValidationMessages,
	country: CountryCode,
	customerAccountNumber?: CustomerAccountNumber
): DebtIssue[] => {
	const result = buildDebtItemSchema(
		messages,
		country,
		customerAccountNumber
	).safeParse(debt);
	const issues: (DebtIssue | undefined)[] = [
		findAmountIssue(debt, messages),
		...(result.success
			? []
			: result.error.issues.map((issue) => ({
					field: issue.path[0] as DebtIssue["field"],
					message: issue.message,
				}))),
		...findRedemptionIssues(debt, messages, country, customerAccountNumber),
	];

	const byField = new Map<DebtIssue["field"], string>();
	for (const issue of issues) {
		if (issue && !byField.has(issue.field)) {
			byField.set(issue.field, issue.message);
		}
	}

	return Array.from(byField, ([field, message]) => ({ field, message }));
};

/** How many of a debt's fields are still empty or invalid. */
export const countIncompleteDebtFields = (
	debt: RefinanceDebt,
	country: CountryCode,
	customerAccountNumber?: CustomerAccountNumber
): number =>
	findDebtIssues(debt, unusedMessages, country, customerAccountNumber).length;

export const isDebtItemComplete = (
	debt: RefinanceDebt,
	country: CountryCode,
	customerAccountNumber?: CustomerAccountNumber
): boolean =>
	countIncompleteDebtFields(debt, country, customerAccountNumber) === 0;

export const needsCompletion = (
	debt: RefinanceDebt,
	country: CountryCode,
	customerAccountNumber?: CustomerAccountNumber
): boolean =>
	isCommittedDebt(debt) &&
	!isDebtItemComplete(debt, country, customerAccountNumber);

interface RefineDebtsArgs {
	messages: DebtValidationMessages;
	offer: RefinanceOfferParts;
	appliedRefinance: number;
	country: CountryCode;
	/** A forced refinance holds a debt the customer added to it, even left empty. */
	isForced: boolean;
	customerAccountNumber?: CustomerAccountNumber;
}

export const refineDebts = (
	data: { debts?: RefinanceDebt[] },
	ctx: z.RefinementCtx,
	{
		messages,
		offer,
		appliedRefinance,
		country,
		isForced,
		customerAccountNumber,
	}: RefineDebtsArgs
) => {
	for (const [index, debt] of (data.debts ?? []).entries()) {
		// A reported balance is known up front, so its amount rules apply before
		// the debt is committed.
		const issues =
			isCommittedDebt(debt) || (isForced && !debt.isReported)
				? findDebtIssues(debt, messages, country, customerAccountNumber)
				: [findAmountIssue(debt, messages)];

		for (const issue of issues) {
			if (!issue) continue;

			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: issue.message,
				path: ["debts", index, issue.field],
			});
		}
	}

	// The totals are explained by the loan overview's allocation bar and alert,
	// so the form-level issue only has to keep the step from being submitted.
	if (
		findRefinanceDebtIssues({
			offer,
			appliedRefinance,
			debts: data.debts ?? [],
		}).length > 0
	) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: messages.required,
			path: ["debts"],
		});
	}
};

export const useDebtValidationMessages = (): DebtValidationMessages => {
	const t = useTranslations("pages.RefinancePage");
	const { format } = useCurrency();

	return {
		required: t("errors.mandatoryField"),
		amountAboveZero: t("errors.amountAboveZero"),
		amountAboveOriginal: (originalAmount) =>
			t("errors.amountAboveOriginal", { amount: format(originalAmount) }),
		invalidPlusgiro: t("errors.invalidPlusgiro"),
		invalidBankgiro: t("errors.invalidBankgiro"),
		invalidGiroCheckDigit: t("errors.invalidGiroCheckDigit"),
		invalidIban: t("errors.invalidIban"),
		ibanIsOwnAccount: t("errors.ibanIsOwnAccount"),
		invalidOcr: t("errors.invalidOcr"),
	};
};

export const useRefinanceForm = ({ context }: UseRefinanceFormArgs) => {
	const country = useCountry();
	const messages = useDebtValidationMessages();

	const { offer, isCoapplicant, appliedRefinance, customerAccountNumber } =
		context;
	const { isForced } = useRefinanceScenario(context);

	const schema = z
		.object({
			appliedAmount: z.number().optional(),
			debts: z.array(z.custom<RefinanceDebt>(() => true)),
		})
		.superRefine((data, ctx) => {
			if (isCoapplicant) return;

			refineDebts(data, ctx, {
				messages,
				offer,
				appliedRefinance,
				country,
				isForced,
				customerAccountNumber,
			});
		});

	return {
		useForm: useForm<RefinanceForm, unknown, RefinanceForm>({
			resolver: zodResolver(schema) as unknown as Resolver<
				RefinanceForm,
				unknown,
				RefinanceForm
			>,
			mode: "onTouched",
			defaultValues: {
				debts: [],
			},
		}),
		schema,
	};
};
